/**
 * SmartRedirect Component
 * Componente che gestisce il redirect basato sulla menu configuration
 * Verifica se ask-eve è abilitato, altrimenti fa fallback a controlHub o containers
 */
import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useMenuConfiguration } from '@/hooks/useMenuConfiguration';
import { useMe } from '@/hooks/api/use-auth';
import { getRedirectPath } from '@/hooks/api/use-auth';
import { CompanyType } from '@/types/api/admin';

interface SmartRedirectProps {
    companyType: CompanyType;
    defaultPath: string;
}

export function SmartRedirect({ companyType, defaultPath }: SmartRedirectProps) {
    const navigate = useNavigate();
    const { data: currentUser } = useMe();
    const { menuConfig, isLoading, isAdmin } = useMenuConfiguration();

    useEffect(() => {
        // Se siamo ancora in loading, aspetta
        if (isLoading) {
            return;
        }

        // Se l'utente non è ancora caricato, aspetta
        if (!currentUser) {
            return;
        }

        // Admin bypassano sempre le restrizioni, usa il default path
        if (isAdmin) {
            navigate(defaultPath, { replace: true });
            return;
        }

        // Crea un oggetto UserInfo minimo per getRedirectPath
        const userInfo = {
            role: currentUser.role,
            company_type: companyType,
            company_id: currentUser.company_id,
        } as any;

        // Determina il redirect path usando la menu configuration
        const redirectPath = getRedirectPath(userInfo, menuConfig);

        if (redirectPath) {
            navigate(redirectPath, { replace: true });
        } else {
            // Fallback al default path se getRedirectPath ritorna null
            navigate(defaultPath, { replace: true });
        }
    }, [menuConfig, isLoading, isAdmin, currentUser, companyType, defaultPath, navigate]);

    // Mostra un loading state mentre determina il redirect
    return (
        <div className="flex items-center justify-center min-h-screen">
            <div className="text-tertiary">Loading...</div>
        </div>
    );
}
